import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/library/card";
import {
  Eye,
  EyeOff,
  Copy,
  Check,
  KeyRound,
  CheckCircle2,
  Shield,
  Lock,
  Phone,
  ToggleLeft,
  ToggleRight,
  Loader2,
  GraduationCap,
} from "lucide-react";
import axiosInstance from "@/lib/axios";
import {
  externalAccessStatusEndpoint,
  externalAccessGenerateEndpoint,
  externalAccessResetEndpoint,
  externalAccessToggleEndpoint,
} from "@/config/endpoints";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";

interface PortalStatus {
  hasAccess: boolean;
  id?: number;
  isActive?: boolean;
  lastLogin?: string;
  loginCount?: number;
  createdAt?: string;
}

interface GeneratedCredentials {
  phone: string;
  password: string;
  leadName?: string;
}

function CredentialRow({
  label,
  value,
  icon: Icon,
  isMasked = false,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  isMasked?: boolean;
}) {
  const [visible, setVisible] = useState(!isMasked);
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-[var(--color-black-1)] last:border-b-0">
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-[var(--color-green-1)] flex items-center justify-center">
          <Icon size={14} className="text-[var(--color-primary)]" />
        </div>
        <div>
          <p className="text-xs font-normal text-[var(--color-black-5)]">
            {label}
          </p>
          <p className="text-sm font-medium text-[var(--color-black-8)] font-mono mt-0.5">
            {visible ? value : "••••••••••"}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        {isMasked && (
          <button
            onClick={() => setVisible(!visible)}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-[var(--color-black-4)] hover:text-[var(--color-black-7)] hover:bg-[var(--color-black-1)] transition-all"
          >
            {visible ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        )}
        <button
          onClick={copy}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-[var(--color-black-4)] hover:text-[var(--color-black-7)] hover:bg-[var(--color-black-1)] transition-all"
        >
          {copied ? (
            <Check size={14} className="text-emerald-500" />
          ) : (
            <Copy size={14} />
          )}
        </button>
      </div>
    </div>
  );
}

const STAGES = ["Leads", "Application", "Visa", "Enrollment"];

function ApplicationProgress({ stageName, status, subStatus }: { stageName: string; status: string; subStatus: string }) {
  const stageIndex = STAGES.indexOf(stageName);
  const resolvedIndex = stageIndex >= 0 ? stageIndex : 0;
  const progress = ((resolvedIndex + 1) / STAGES.length) * 100;

  return (
    <Card className="border-[var(--color-black-1)] rounded-[16px] gap-0 py-0 shadow-none">
      <div className="px-4 py-3 border-b border-[var(--color-black-1)]">
        <p className="text-sm font-medium text-[var(--color-black-8)]">Application Progress</p>
      </div>
      <CardContent className="px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          {STAGES.map((stage, i) => {
            const isPast = i <= resolvedIndex;
            const isCurrent = i === resolvedIndex;
            return (
              <div key={stage} className="flex-1 flex flex-col items-center gap-2 relative">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-all ${
                    isCurrent
                      ? "bg-[var(--color-primary)] text-white border-transparent scale-110"
                      : isPast
                        ? "bg-[var(--color-green-2)] text-[var(--color-primary)] border-[var(--color-green-3)]"
                        : "bg-[var(--color-black-1)] text-[var(--color-black-4)] border-[var(--color-black-2)]"
                  }`}
                >
                  {isPast ? <CheckCircle2 size={16} /> : i + 1}
                </div>
                <span
                  className={`text-[11px] ${
                    isCurrent
                      ? "font-medium text-[var(--color-primary)]"
                      : "font-normal text-[var(--color-black-5)]"
                  }`}
                >
                  {stage}
                </span>

                {i < STAGES.length - 1 && (
                  <div
                    className="absolute top-[18px] left-[calc(50%+18px)] h-0.5 z-0"
                    style={{
                      width: "calc(100% - 36px)",
                      background: isPast && i < resolvedIndex ? "var(--color-primary)" : "var(--color-black-2)",
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1 bg-[var(--color-black-1)] rounded-full h-1.5">
            <div
              className="h-1.5 rounded-full transition-all"
              style={{ width: `${progress}%`, background: "var(--color-primary)" }}
            />
          </div>
          <span className="text-xs font-medium text-[var(--color-primary)]">{Math.round(progress)}%</span>
        </div>

        <div className="mt-3 px-3 py-2 rounded-[10px] bg-[var(--color-green-1)] border border-[var(--color-green-3)]">
          <p className="text-xs font-normal text-[var(--color-primary)]">
            <span className="font-medium">Current:</span> {status} — {subStatus}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function StudentInfo({ lead }: { lead: any }) {
  const items = [
    { label: "Stage", value: lead?.stage?.name ?? "—" },
    { label: "Status", value: lead?.status?.name ?? "—" },
    { label: "Sub Status", value: lead?.subStatus?.name ?? "—" },
    { label: "Phone", value: lead?.phone ?? "—" },
  ];

  return (
    <Card className="border-[var(--color-black-1)] rounded-[16px] gap-0 py-0 shadow-none">
      <div className="px-4 py-3 border-b border-[var(--color-black-1)]">
        <p className="text-sm font-medium text-[var(--color-black-8)]">Student Info</p>
      </div>
      <CardContent className="px-4 py-3">
        <div className="grid grid-cols-2 gap-3">
          {items.map((item) => (
            <div key={item.label} className="flex items-start gap-2 p-2.5 rounded-[10px] bg-[var(--color-black-1)]/50">
              <GraduationCap size={14} className="text-[var(--color-black-4)] mt-0.5 shrink-0" />
              <div>
                <p className="text-[11px] font-normal text-[var(--color-black-4)]">{item.label}</p>
                <p className="text-sm font-medium text-[var(--color-black-8)] mt-0.5">{item.value}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Portal() {
  const { lead } = useActiveDataMutation();
  const queryClient = useQueryClient();
  const leadId = lead?.id;

  const [generatedCreds, setGeneratedCreds] = useState<GeneratedCredentials | null>(null);

  const featureKey = "student_portal";
  const queryKey = ["external-access-status", featureKey, leadId];

  const { data: portalStatus, isLoading } = useQuery<PortalStatus>({
    queryKey,
    queryFn: async () => {
      const res = await axiosInstance.get(externalAccessStatusEndpoint(featureKey, leadId));
      return res.data.data;
    },
    enabled: !!leadId,
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post(externalAccessGenerateEndpoint(featureKey, leadId));
      return res.data.data as GeneratedCredentials;
    },
    onSuccess: (data) => {
      setGeneratedCreds(data);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post(externalAccessResetEndpoint(featureKey, leadId));
      return res.data.data as GeneratedCredentials;
    },
    onSuccess: (data) => {
      setGeneratedCreds(data);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async (isActive: boolean) => {
      const res = await axiosInstance.patch(externalAccessToggleEndpoint(featureKey, leadId), { isActive });
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const lastLoginDate = useMemo(() => {
    if (!portalStatus?.lastLogin) return null;
    return new Date(portalStatus.lastLogin).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [portalStatus?.lastLogin]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 size={20} className="animate-spin text-[var(--color-black-4)]" />
      </div>
    );
  }

  if (!portalStatus?.hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-[var(--color-black-1)] flex items-center justify-center">
          <Shield size={24} className="text-[var(--color-black-4)]" />
        </div>
        <div>
          <p className="text-sm font-medium text-[var(--color-black-8)]">
            No Portal Access
          </p>
          <p className="text-xs font-normal text-[var(--color-black-5)] mt-1 max-w-[280px]">
            Generate portal credentials so this student can log in and view their application progress.
          </p>
        </div>
        <button
          onClick={() => generateMutation.mutate()}
          disabled={generateMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 rounded-[var(--btn-radius)] text-sm font-medium text-white transition-all hover:opacity-90 disabled:opacity-50"
          style={{ background: "var(--color-primary)" }}
        >
          {generateMutation.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <KeyRound size={14} />
          )}
          Generate Credentials
        </button>

        {generatedCreds && (
          <Card className="border-[var(--color-green-3)] bg-[var(--color-green-1)] rounded-[16px] gap-0 py-0 shadow-none w-full max-w-sm">
            <CardContent className="px-4 py-3">
              <p className="text-xs font-medium text-[var(--color-primary)] mb-2">
                Credentials generated — share these with the student:
              </p>
              <CredentialRow
                label="Phone"
                value={generatedCreds.phone}
                icon={Phone}
              />
              <CredentialRow
                label="Password"
                value={generatedCreds.password}
                icon={Lock}
                isMasked
              />
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  const stageName = lead?.stage?.name ?? "";
  const statusName = lead?.status?.name ?? "";
  const subStatusName = lead?.subStatus?.name ?? "";

  return (
    <div className="flex flex-col gap-3 w-full p-4">
      {/* Portal Status */}
      <Card className="border-[var(--color-black-1)] rounded-[16px] gap-0 py-0 shadow-none overflow-hidden">
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{
            background: portalStatus.isActive
              ? "linear-gradient(135deg, var(--color-green-1) 0%, var(--color-green-2) 100%)"
              : "var(--color-black-1)",
          }}
        >
          <div className="flex items-center gap-2.5">
            <Shield
              size={16}
              className={
                portalStatus.isActive
                  ? "text-[var(--color-primary)]"
                  : "text-[var(--color-black-5)]"
              }
            />
            <div>
              <p className="text-sm font-medium text-[var(--color-black-8)]">
                Student Portal Access
              </p>
              <p className="text-xs font-normal text-[var(--color-black-5)] mt-0.5">
                {portalStatus.isActive
                  ? `Active${lastLoginDate ? ` · Last login ${lastLoginDate}` : ""}`
                  : "Portal access is disabled for this student"}
              </p>
            </div>
          </div>
          <button
            onClick={() => toggleMutation.mutate(!portalStatus.isActive)}
            disabled={toggleMutation.isPending}
            className="transition-colors disabled:opacity-50"
          >
            {portalStatus.isActive ? (
              <ToggleRight size={32} className="text-[var(--color-primary)]" />
            ) : (
              <ToggleLeft size={32} className="text-[var(--color-black-3)]" />
            )}
          </button>
        </div>

        {portalStatus.isActive && (
          <div className="flex items-center gap-4 px-4 py-2.5 border-t border-[var(--color-black-1)] bg-white">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              <span className="text-xs font-normal text-[var(--color-black-5)]">
                Active
              </span>
            </div>
            <span className="text-xs font-normal text-[var(--color-black-4)]">
              ·
            </span>
            <span className="text-xs font-normal text-[var(--color-black-5)]">
              {portalStatus.loginCount ?? 0} logins
            </span>
          </div>
        )}
      </Card>

      {/* Login Credentials */}
      <Card className="border-[var(--color-black-1)] rounded-[16px] gap-0 py-0 shadow-none">
        <div className="px-4 py-3 border-b border-[var(--color-black-1)]">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-[var(--color-black-8)]">
              Login Credentials
            </p>
            <button
              onClick={() => resetPasswordMutation.mutate()}
              disabled={resetPasswordMutation.isPending}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-[var(--btn-radius)] text-xs font-medium transition-all hover:opacity-80 disabled:opacity-50"
              style={{
                background: "var(--color-black-1)",
                color: "var(--color-black-7)",
              }}
            >
              {resetPasswordMutation.isPending ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <KeyRound size={12} />
              )}
              Reset Password
            </button>
          </div>
        </div>
        <CardContent className="px-4 py-0">
          <CredentialRow
            label="Phone Number"
            value={lead?.phone ?? "—"}
            icon={Phone}
          />
        </CardContent>
      </Card>

      {/* Generated credentials banner */}
      {generatedCreds && (
        <Card className="border-[var(--color-green-3)] bg-[var(--color-green-1)] rounded-[16px] gap-0 py-0 shadow-none">
          <CardContent className="px-4 py-3">
            <p className="text-xs font-medium text-[var(--color-primary)] mb-2">
              New credentials generated — share with the student:
            </p>
            <CredentialRow
              label="Phone"
              value={generatedCreds.phone}
              icon={Phone}
            />
            <CredentialRow
              label="New Password"
              value={generatedCreds.password}
              icon={Lock}
              isMasked
            />
          </CardContent>
        </Card>
      )}

      {/* Application Progress */}
      <ApplicationProgress
        stageName={stageName}
        status={statusName}
        subStatus={subStatusName}
      />

      {/* Student Info */}
      <StudentInfo lead={lead} />
    </div>
  );
}
